import React from 'react';
import {CompetitionDto} from "@/src/entities/competition/model/types";
import {backendDateToString} from "@/src/shared/utils/date";
import Link from "next/link";
import {ArrowRight, Calendar, MapPin, Trophy, Image as ImageIcon} from "lucide-react";
import {useGetDeclarationStatus} from "@/src/entities/competition/lib/getDeclarationStatus";
import CompetitionPoster from "@/src/entities/competition/ui/CompetitionPoster";
import DeclarationStatusBadge from "@/src/entities/competition/ui/DeclarationStatusBadge";
import {useTranslations} from "next-intl";

interface Props {
    competition: CompetitionDto
    path: string
}

function Competition({competition, path}: Props) {
    const t = useTranslations("competitions");
    const getDeclarationStatus = useGetDeclarationStatus();
    const status = getDeclarationStatus(competition);

    return (
        <div className="group relative flex w-full flex-col sm:flex-row gap-5 rounded-2xl border border-neutral-200 bg-white p-4 text-black transition hover:border-blue-300 hover:shadow-lg hover:shadow-blue-900/5">
            {/* Ссылка растянута на всю карточку, чтобы кнопка результатов не оказалась ссылкой внутри ссылки */}
            <Link
                href={`${path}${competition.slug ?? competition.id}`}
                aria-label={competition.title}
                className="absolute inset-0 z-10 rounded-2xl"
            />
            <div className="sm:w-44 sm:min-w-44">
                {competition.logo || competition.logo_video ? (
                    <CompetitionPoster
                        logo={competition.logo}
                        logoVideo={competition.logo_video}
                        alt={competition.title}
                        className="h-48 w-full rounded-xl bg-neutral-50 object-contain sm:h-40"
                    />
                ) : (
                    <div className="flex h-48 w-full items-center justify-center rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 sm:h-40">
                        <ImageIcon size={36} className="text-blue-300" />
                    </div>
                )}
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="text-xl sm:text-2xl font-bold leading-tight transition group-hover:text-blue-700">
                        {competition.title}
                    </div>
                    <DeclarationStatusBadge status={status} />
                </div>

                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-base text-neutral-600">
                    <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-neutral-400" />
                        {backendDateToString(competition.start_date)} — {backendDateToString(competition.end_date)}
                    </div>
                    {competition.place && (
                        <div className="flex items-center gap-2">
                            <MapPin size={16} className="text-neutral-400" />
                            {competition.place}
                        </div>
                    )}
                </div>

                {competition.description && (
                    <div className="line-clamp-2 text-base text-neutral-700">
                        {competition.description}
                    </div>
                )}

                <div className="relative z-20 mt-auto flex flex-wrap items-center gap-2 pt-1">
                    {competition.has_results && (
                        <Link
                            href={`/competitions/${competition.slug ?? competition.id}/results`}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-1.5 text-sm font-semibold text-neutral-600 transition hover:border-blue-300 hover:text-blue-700"
                        >
                            <Trophy size={14} />
                            {t("results")}
                        </Link>
                    )}
                    <span className="pointer-events-none ml-auto hidden items-center gap-1 text-base font-semibold text-blue-600 opacity-0 transition group-hover:opacity-100 sm:flex">
                        {t("more")} <ArrowRight size={16} />
                    </span>
                </div>
            </div>
        </div>
    );
}

export default Competition;
